'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const primaryNavItems = [
    { label: 'Home', href: '/homepage' },
    { label: 'Our Team', href: '/our-gen-carbon-team' },
    { label: 'Our Services', href: '/our-services' },
    { label: 'Impact Stories', href: '/impact-stories' },
    { label: 'Golf Course Hub', href: '/golf-course-hub' },
  ];

  const secondaryNavItems = [
    { label: 'Carbon Credits', href: '/carbon-credits-explained' },
    { label: 'ESG Reporting', href: '/esg-reporting-solutions' },
    { label: 'Industry Solutions', href: '/industry-specific-esg-solutions' },
    { label: 'BRSR & ESG Hub', href: '/brsr-esg-compliance-hub' },
    { label: 'Emission Calculator', href: '/ai-powered-emission-calculation-platform' },
    { label: 'Enterprise Solutions', href: '/enterprise-government-solutions' },
    { label: 'Compliance Tracking', href: '/compliance-tracking-submission' },
    { label: "Let\'s Collaborate", href: '/let-s-collaborate' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsMoreMenuOpen(false);
  };

  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  return (
    <header className={`bg-card border-b border-border sticky top-0 z-50 ${className}`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 md:px-6 lg:px-8">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-3 flex-shrink-0">
            <Image
              src="/assets/images/gencarbon_logo-1767797913709.png"
              alt="GenCarbon ESG - Eye of RA logo with Go Green Earn Green tagline"
              width={180}
              height={45}
              className="h-12 w-auto object-contain"
              priority
              unoptimized
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="px-4 py-2 text-sm font-medium text-text-primary hover:text-primary hover:bg-surface rounded-lg transition-all duration-300 ease-organic"
              >
                {item.label}
              </Link>
            ))}

            {/* More Menu */}
            <div className="relative">
              <button
                onClick={toggleMoreMenu}
                className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-text-primary hover:text-primary hover:bg-surface rounded-lg transition-all duration-300 ease-organic"
              >
                <span>More</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`transform transition-transform duration-300 ${
                    isMoreMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isMoreMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-natural-lg overflow-hidden animate-fade-in">
                  {secondaryNavItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsMoreMenuOpen(false)}
                      className="block px-4 py-3 text-sm font-medium text-text-primary hover:bg-surface hover:text-primary transition-colors duration-200"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center space-x-3 ml-4">
            {/* Email Button */}
            <a
              href="mailto:dr.nupur@gencarbon.in"
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-text-primary hover:text-primary hover:bg-surface rounded-lg transition-all duration-300 ease-organic"
              aria-label="Email us"
            >
              <Icon name="EnvelopeIcon" size={18} />
              <span>Email us</span>
            </a>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/919876543210"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-text-primary hover:text-primary hover:bg-surface rounded-lg transition-all duration-300 ease-organic"
              aria-label="WhatsApp"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={18} />
              <span>WhatsApp</span>
            </a>

            {/* Pilot With us for Carbon Credits Button */}
            <a
              href="mailto:nupurbanker@gmail.com?subject=Carbon Credits Pilot Program Inquiry"
              className="flex items-center space-x-2 px-5 py-2.5 bg-[#1a1a1a] text-white font-heading font-semibold text-sm rounded-lg hover:bg-[#2d2d2d] shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
            >
              <Icon name="SparklesIcon" size={18} />
              <span>Pilot With us for Carbon Credits</span>
            </a>

            {/* MOU or JV with Us Button */}
            <Link
              href="/let-s-collaborate"
              className="flex items-center space-x-2 px-5 py-2.5 bg-primary text-primary-foreground font-heading font-semibold text-sm rounded-lg hover:bg-primary/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
            >
              <Icon name="UserGroupIcon" size={18} />
              <span>MOU or JV with Us</span>
            </Link>

            {/* Get Started Button */}
            <Link
              href="/get-started"
              className="px-6 py-2.5 bg-conversion text-conversion-foreground font-heading font-semibold text-sm rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-text-primary hover:text-primary hover:bg-surface rounded-lg transition-colors duration-200"
            aria-label="Toggle menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-card animate-fade-in">
            <nav className="px-4 py-4 space-y-1">
              {primaryNavItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-sm font-medium text-text-primary hover:text-primary hover:bg-surface rounded-lg transition-colors duration-200"
                >
                  {item.label}
                </Link>
              ))}

              <div className="pt-2 border-t border-border mt-2">
                {secondaryNavItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-text-primary hover:text-primary hover:bg-surface rounded-lg transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Mobile CTA Buttons */}
              <div className="pt-4 space-y-2">
                <a
                  href="mailto:dr.nupur@gencarbon.in"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-surface text-text-primary font-heading font-semibold text-sm rounded-lg hover:bg-surface/80 transition-all duration-300 ease-organic"
                >
                  <Icon name="EnvelopeIcon" size={18} />
                  <span>Email us</span>
                </a>
                
                <a
                  href="https://wa.me/919876543210"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-surface text-text-primary font-heading font-semibold text-sm rounded-lg hover:bg-surface/80 transition-all duration-300 ease-organic"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={18} />
                  <span>WhatsApp</span>
                </a>

                <a
                  href="mailto:nupurbanker@gmail.com?subject=Carbon Credits Pilot Program Inquiry"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-[#1a1a1a] text-white font-heading font-semibold text-sm rounded-lg hover:bg-[#2d2d2d] shadow-natural transition-all duration-300 ease-organic"
                >
                  <Icon name="SparklesIcon" size={18} />
                  <span>Pilot With us for Carbon Credits</span>
                </a>

                <Link
                  href="/let-s-collaborate"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-primary text-primary-foreground font-heading font-semibold text-sm rounded-lg hover:bg-primary/90 shadow-natural transition-all duration-300 ease-organic"
                >
                  <Icon name="UserGroupIcon" size={18} />
                  <span>MOU or JV with Us</span>
                </Link>

                <Link
                  href="/get-started"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full px-6 py-3 bg-conversion text-conversion-foreground font-heading font-semibold text-sm text-center rounded-lg hover:bg-conversion/90 shadow-natural transition-all duration-300 ease-organic"
                >
                  Get Started
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;